import React, { useEffect, useRef, useState } from 'react';

// Cache the public config + script load across mounts so we hit the API once
// and never inject the Google script twice.
let configPromise = null;
function fetchCaptchaConfig() {
  if (!configPromise) {
    configPromise = fetch('/api/config/public')
      .then(r => r.json())
      .then(d => d.recaptcha || { enabled: false })
      .catch(() => ({ enabled: false }));
  }
  return configPromise;
}

let scriptPromise = null;
function loadRecaptchaScript() {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      s.async = true; s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * Google reCAPTCHA v2 checkbox. Renders nothing when captcha is disabled
 * globally or for this `page`. Reports the token (or '' on expiry) via onToken.
 *
 * Props: page = 'login' | 'forgot' | 'recruitment', onToken(token)
 */
const Recaptcha = ({ page, onToken }) => {
  const boxRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [siteKey, setSiteKey] = useState('');
  const [active, setActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCaptchaConfig().then(rc => {
      if (cancelled) return;
      if (rc.enabled && rc.pages && rc.pages[page] && rc.siteKey) {
        setSiteKey(rc.siteKey);
        setActive(true);
      }
    });
    return () => { cancelled = true; };
  }, [page]);

  useEffect(() => {
    if (!active || !siteKey) return;
    let cancelled = false;
    loadRecaptchaScript().then(() => {
      if (cancelled || !boxRef.current || widgetIdRef.current !== null) return;
      const doRender = () => {
        if (!window.grecaptcha || !window.grecaptcha.render) { setTimeout(doRender, 200); return; }
        try {
          widgetIdRef.current = window.grecaptcha.render(boxRef.current, {
            sitekey: siteKey,
            callback: (token) => onToken && onToken(token),
            'expired-callback': () => onToken && onToken(''),
            'error-callback': () => onToken && onToken('')
          });
        } catch (_) { /* already rendered */ }
      };
      doRender();
    }).catch(() => { /* script blocked — leave form usable, server still gates */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, siteKey]);

  if (!active) return null;
  return <div className="recaptcha-box" ref={boxRef} style={{ margin: '4px 0 16px' }} />;
};

export default Recaptcha;
